import React, { useState, useEffect } from 'react';

const HowWeWorkSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [hoveredImage, setHoveredImage] = useState(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const containerStyle = {
    minHeight: '100vh',
    padding: '20px 20px 80px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    position: 'relative'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '80px',
    transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
    opacity: isVisible ? 1 : 0,
    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  const mainTitleStyle = {
    fontSize: '48px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '20px',
    letterSpacing: '-1px'
  };

  const subtitleStyle = {
    fontSize: '18px',
    color: '#64748b',
    maxWidth: '600px',
    margin: '0 auto',
    lineHeight: '1.6'
  };

  const contentContainerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '100px'
  };

  // First section (image left, content right)
  const sectionStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '60px',
    flexWrap: 'wrap'
  };

  const reverseSectionStyle = {
    ...sectionStyle,
    flexDirection: 'row-reverse'
  };

  const imageContainerStyle = {
    flex: '1',
    minWidth: '300px',
    position: 'relative'
  };

  const getImageStyle = (index) => ({
    width: '100%',
    height: '400px',
    borderRadius: '20px',
    objectFit: 'cover',
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: hoveredImage === index ? 'scale(1.02) translateY(-5px)' : 'scale(1) translateY(0)',
    filter: hoveredImage === index ? 'brightness(1.05)' : 'brightness(1)'
  });

  const imageCaptionStyle = {
    textAlign: 'center',
    fontSize: '14px',
    color: '#94a3b8',
    fontStyle: 'italic',
    marginTop: '15px',
    letterSpacing: '0.5px'
  };

  const contentStyle = {
    flex: '1',
    minWidth: '300px',
    maxWidth: '500px'
  };

  const sectionHeaderStyle = {
    fontSize: '12px',
    fontWeight: '700',
    color: '#94a3b8',
    letterSpacing: '2px',
    marginBottom: '20px',
    textTransform: 'uppercase'
  };

  const sectionTitleStyle = {
    fontSize: '36px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '24px',
    lineHeight: '1.3',
    letterSpacing: '-0.5px'
  };

  const sectionTextStyle = {
    fontSize: '16px',
    color: '#64748b',
    lineHeight: '1.7',
    marginBottom: '32px'
  };

  const getButtonStyle = (index) => ({
    backgroundColor: hoveredButton === index ? '#3b82f6' : '#60a5fa',
    color: 'white',
    padding: '14px 28px',
    borderRadius: '25px',
    border: 'none',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: hoveredButton === index ? 'translateY(-2px)' : 'translateY(0)',
    boxShadow: hoveredButton === index 
      ? '0 8px 25px rgba(59, 130, 246, 0.4)' 
      : '0 4px 15px rgba(96, 165, 250, 0.3)',
    textDecoration: 'none',
    display: 'inline-block'
  });

  // Animation delays for staggered entrance
  const getAnimatedStyle = (delay = 0) => ({
    transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
    opacity: isVisible ? 1 : 0,
    transition: `all 0.8s cubic-bezier(0.4, 0, 0.2, 1) ${delay}s`
  });

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h1 style={mainTitleStyle}>How We Work</h1>
        <p style={subtitleStyle}>
          Our research-driven approach to transforming AI education globally.
        </p>
      </div>

      {/* Content Sections */}
      <div style={contentContainerStyle}>
        {/* First Section */}
        <div style={{...sectionStyle, ...getAnimatedStyle(0.2)}}>
          <div 
            style={imageContainerStyle}
            onMouseEnter={() => setHoveredImage(0)}
            onMouseLeave={() => setHoveredImage(null)}
          >
            <img 
              src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxkZWZzPgo8bGluZWFyR3JhZGllbnQgaWQ9ImJnMSIgeDE9IjAlIiB5MT0iMCUiIHgyPSIxMDAlIiB5Mj0iMTAwJSI+CjxzdG9wIG9mZnNldD0iMCUiIHN0eWxlPSJzdG9wLWNvbG9yOiNmMWY1Zjk7c3RvcC1vcGFjaXR5OjEiIC8+CjxzdG9wIG9mZnNldD0iMTAwJSIgc3R5bGU9InN0b3AtY29sb3I6I2U3ZWVmZjtzdG9wLW9wYWNpdHk6MSIgLz4KPC9saW5lYXJHcmFkaWVudD4KPC9kZWZzPgo8cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0idXJsKCNiZzEpIi8+CjwhLS0gTGFwdG9wIC0tPgo8cmVjdCB4PSI1MCIgeT0iMTAwIiB3aWR0aD0iMzAwIiBoZWlnaHQ9IjE4MCIgcng9IjEwIiBmaWxsPSIjMzMzIi8+CjxyZWN0IHg9IjYwIiB5PSIxMTAiIHdpZHRoPSIyODAiIGhlaWdodD0iMTUwIiByeD0iNSIgZmlsbD0iIzAwMCIvPgo8IS0tIFNjcmVlbiBjb250ZW50IC0tPgo8cmVjdCB4PSI3MCIgeT0iMTIwIiB3aWR0aD0iMjYwIiBoZWlnaHQ9IjEzMCIgZmlsbD0iIzFhMjAyYyIvPgo8IS0tIENvZGUgbGluZXMgLS0+CjxyZWN0IHg9IjgwIiB5PSIxMzAiIHdpZHRoPSIxMDAiIGhlaWdodD0iNCIgZmlsbD0iIzYwYTVmYSIvPgo8cmVjdCB4PSI4MCIgeT0iMTQwIiB3aWR0aD0iMTUwIiBoZWlnaHQ9IjQiIGZpbGw9IiNlYmY4ZmYiLz4KPHJlY3QgeD0iODAiIHk9IjE1MCIgd2lkdGg9IjEyMCIgaGVpZ2h0PSI0IiBmaWxsPSIjZmY2YjZiIi8+CjxyZWN0IHg9IjgwIiB5PSIxNjAiIHdpZHRoPSIxODAiIGhlaWdodD0iNCIgZmlsbD0iIzM0ZDM5OSIvPgo8cmVjdCB4PSI4MCIgeT0iMTcwIiB3aWR0aD0iOTAiIGhlaWdodD0iNCIgZmlsbD0iI2ZiYmYyNCIvPgo8IS0tIEhhbmRzIG9uIGtleWJvYXJkIC0tPgo8ZWxsaXBzZSBjeD0iMTUwIiBjeT0iMjcwIiByeD0iNDAiIHJ5PSIxNSIgZmlsbD0iI2ZkYjY4NyIgb3BhY2l0eT0iMC44Ii8+CjxlbGxpcHNlIGN4PSIyNTAiIGN5PSIyNzAiIHJ4PSI0MCIgcnk9IjE1IiBmaWxsPSIjZmRiNjg3IiBvcGFjaXR5PSIwLjgiLz4KPC9zdmc+"
              alt="Person working on laptop"
              style={getImageStyle(0)}
            />
            <p style={imageCaptionStyle}>Towards accessible AI education</p>
          </div>
          
          <div style={contentStyle}>
            <div style={sectionHeaderStyle}>OUR EDUCATIONAL PATH</div>
            <h2 style={sectionTitleStyle}>
              How does Global AI Society revolutionize AI education?
            </h2>
            <p style={sectionTextStyle}>
              What do Collaborative Learning and our Global Network 
              bring to the table? The synergy of human creativity and the experience 
              of thousands of educators with the fantastic power of AI and modern 
              learning technologies, is the inevitable future. However, to find solutions 
              to educational challenges, another element is needed: aligning AI 
              education with human values and accessibility.
            </p>
          </div>
        </div>

        {/* Second Section */}
        <div style={{...reverseSectionStyle, ...getAnimatedStyle(0.4)}}>
          <div 
            style={imageContainerStyle}
            onMouseEnter={() => setHoveredImage(1)}
            onMouseLeave={() => setHoveredImage(null)}
          >
            <img 
              src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxkZWZzPgo8bGluZWFyR3JhZGllbnQgaWQ9ImJnMiIgeDE9IjAlIiB5MT0iMCUiIHgyPSIxMDAlIiB5Mj0iMTAwJSI+CjxzdG9wIG9mZnNldD0iMCUiIHN0eWxlPSJzdG9wLWNvbG9yOiNmMWY1Zjk7c3RvcC1vcGFjaXR5OjEiIC8+CjxzdG9wIG9mZnNldD0iMTAwJSIgc3R5bGU9InN0b3AtY29sb3I6I2RkZDZmZTtzdG9wLW9wYWNpdHk6MSIgLz4KPC9saW5lYXJHcmFkaWVudD4KPC9kZWZzPgo8cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0idXJsKCNiZzIpIi8+CjwhLS0gUGVyc29uIGF0IGRlc2sgLS0+CjxjaXJjbGUgY3g9IjE1MCIgY3k9IjEwMCIgcj0iMjUiIGZpbGw9IiNmZGI2ODciLz4KPHJlY3QgeD0iMTMwIiB5PSIxMjAiIHdpZHRoPSI0MCIgaGVpZ2h0PSI2MCIgcng9IjgiIGZpbGw9IiM0YzE5NjgiLz4KPCEtLSBEZXNrIC0tPgo8cmVjdCB4PSI1MCIgeT0iMTgwIiB3aWR0aD0iMzAwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjNjY2Ii8+CjwhLS0gQ29tcHV0ZXIgc2NyZWVuIC0tPgo8cmVjdCB4PSIyMDAiIHk9IjEwMCIgd2lkdGg9IjEyMCIgaGVpZ2h0PSI4MCIgcng9IjUiIGZpbGw9IiMzMzMiLz4KPHJlY3QgeD0iMjA1IiB5PSIxMDUiIHdpZHRoPSIxMTAiIGhlaWdodD0iNzAiIGZpbGw9IiMxYTIwMmMiLz4KPCEtLSBMYWIgZXF1aXBtZW50IC0tPgo8Y2lyY2xlIGN4PSI4MCIgY3k9IjEyMCIgcj0iMTUiIGZpbGw9IiM2MzY2ZjEiIG9wYWNpdHk9IjAuNyIvPgo8Y2lyY2xlIGN4PSI2MCIgY3k9IjE0MCIgcj0iMTIiIGZpbGw9IiNlYjQ0OWIiIG9wYWNpdHk9IjAuNyIvPgo8Y2lyY2xlIGN4PSIxMDAiIGN5PSIxNTAiIHI9IjEwIiBmaWxsPSIjMTBiOTgxIiBvcGFjaXR5PSIwLjciLz4KPCEtLSBDb25uZWN0aW9uIGxpbmVzIC0tPgo8bGluZSB4MT0iMTAwIiB5MT0iMTUwIiB4Mj0iMjAwIiB5Mj0iMTQwIiBzdHJva2U9IiM2MEE1RkEiIHN0cm9rZS13aWR0aD0iMiIgb3BhY2l0eT0iMC41Ii8+CjxsaW5lIHgxPSI4MCIgeTE9IjEyMCIgeDI9IjE1MCIgeTI9IjEwMCIgc3Ryb2tlPSIjNjBBNUZBIiBzdHJva2Utd2lkdGg9IjIiIG9wYWNpdHk9IjAuNSIvPgo8L3N2Zz4="
              alt="Person working in laboratory"
              style={getImageStyle(1)}
            />
            <p style={imageCaptionStyle}>The role of education in creating a better future</p>
          </div>
          
          <div style={contentStyle}>
            <div style={sectionHeaderStyle}>MAKING AN IMPACT THROUGH EDUCATION</div>
            <h2 style={sectionTitleStyle}>
              How is Global AI Society making a difference?
            </h2>
            <p style={sectionTextStyle}>
              To effectively harness the power of AI education and learning 
              technologies, it's essential to align them with human values to ensure 
              accessibility, ethics, and accountability. Our initiatives combine human 
              creativity with AI, fostering interdisciplinary collaboration and open 
              knowledge exchange. Our programs bring together diverse learners to 
              tackle global AI challenges through education.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowWeWorkSection;